import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const QUICK_PAGES = [
  {
    icon: '🏠',
    title: 'الرئيسية',
    description: 'الواجهة الرئيسية والتنبيهات السريعة',
    path: '/',
    keywords: 'home dashboard main بداية واجهة تنبيهات ملخص'
  },
  {
    icon: '📊',
    title: 'لوحة التحكم',
    description: 'الإحصائيات والمؤشرات المالية',
    path: '/dashboard',
    keywords: 'احصائيات dashboard لوحة مؤشرات ارقام اليوم'
  },
  {
    icon: '🏪',
    title: 'المحلات',
    description: 'إدارة المحلات والأرصدة والاسترجاع',
    path: '/stores',
    keywords: 'محل محلات زبائن عملاء دين رصيد تعطيل استرجاع stores'
  },
  {
    icon: '💳',
    title: 'العمليات المالية',
    description: 'شحن، قبض، تحويل، مصروف، وتسديد',
    path: '/transactions',
    keywords: 'عملية عمليات شحن قبض تحويل مصروف تسديد ايصال طباعة transactions'
  },
  {
    icon: '🔀',
    title: 'نقل دين بين المحلات',
    description: 'نقل دين مباشر من محل إلى محل بدون محفظة',
    path: '/store-debt-transfer',
    keywords: 'نقل دين تحويل دين بين المحلات دين محل تحمل دين store debt transfer'
  },
  {
    icon: '💰',
    title: 'المحافظ',
    description: 'كاش، الدولي، كروت محلية، وكشوفات',
    path: '/wallets',
    keywords: 'محفظة محافظ كاش دولي كروت كشف wallet wallets'
  },
  {
    icon: '💼',
    title: 'رصيدي',
    description: 'رصيد المستخدم وتأكيد التحويلات',
    path: '/user-balance',
    keywords: 'رصيدي رصيد المستخدم تحويلات معلقة قبول تاكيد user balance'
  },
  {
    icon: '🏦',
    title: 'طلبات مصرفي',
    description: 'طلبات مصرفي المعلقة والمؤكدة',
    path: '/masrafi-requests',
    keywords: 'مصرفي طلبات وصل تحويل شركة masrafi'
  },
  {
    icon: '👨‍💼',
    title: 'الموظفين',
    description: 'المستخدمين، الصلاحيات، وآخر ظهور',
    path: '/employees',
    keywords: 'موظفين مستخدمين صلاحيات كلمة مرور اخر ظهور employees users'
  },
  {
    icon: '📈',
    title: 'التقارير',
    description: 'تقارير وكشوفات وطباعة',
    path: '/reports',
    keywords: 'تقارير تقرير كشف يوم شهر طباعة reports pdf excel'
  },
  {
    icon: '🔔',
    title: 'الإشعارات',
    description: 'إشعارات النظام والتنبيهات العامة',
    path: '/notifications',
    keywords: 'اشعارات تنبيهات عام رسالة notifications alerts'
  },
  {
    icon: '📋',
    title: 'سجل التدقيق',
    description: 'سجل الحركات والتعديلات',
    path: '/audit-log',
    keywords: 'سجل تدقيق حركات audit log متابعة تعديل حذف'
  },
  {
    icon: '🗃️',
    title: 'الأرشيف',
    description: 'الأرشيف والسجلات القديمة',
    path: '/archive',
    keywords: 'ارشيف archive قديم محفوظات'
  },
  {
    icon: '⚙️',
    title: 'الإعدادات',
    description: 'إعدادات النظام والنسخ الاحتياطي',
    path: '/settings',
    keywords: 'اعدادات نسخ احتياطي شركة settings backup'
  }
];

function normalizeArabic(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[أإآا]/g, 'ا')
    .replace(/[ى]/g, 'ي')
    .replace(/[ؤ]/g, 'و')
    .replace(/[ئ]/g, 'ي')
    .replace(/[ة]/g, 'ه')
    .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
    .replace(/\s+/g, ' ')
    .trim();
}

function scorePage(page, query) {
  const haystack = normalizeArabic([
    page.title,
    page.description,
    page.path,
    page.keywords
  ].join(' '));

  if (!query) return 10;
  if (normalizeArabic(page.title) === query) return 100;
  if (normalizeArabic(page.title).startsWith(query)) return 90;
  if (haystack.includes(query)) return 70;

  const queryParts = query.split(' ').filter(Boolean);
  const matchedParts = queryParts.filter(part => haystack.includes(part)).length;
  return matchedParts ? 35 + matchedParts * 8 : 0;
}

function GlobalQuickMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const isLogin = location.pathname === '/login';

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    setTimeout(() => inputRef.current?.focus(), 80);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setQuery('');
  }, [location.pathname]);

  const results = useMemo(() => {
    const normalizedQuery = normalizeArabic(query);

    return QUICK_PAGES
      .map(page => ({ page, score: scorePage(page, normalizedQuery) }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title, 'ar'))
      .slice(0, normalizedQuery ? 9 : QUICK_PAGES.length)
      .map(item => item.page);
  }, [query]);

  if (isLogin) return null;

  const goToPage = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  return (
    <div className="global-quick-menu" ref={menuRef} dir="rtl">
      <button
        type="button"
        className={`quick-menu-trigger ${open ? 'quick-menu-trigger-open' : ''}`}
        onClick={() => setOpen(value => !value)}
        aria-label="قائمة الوصول السريع"
        title="قائمة الوصول السريع"
      >
        <span className="quick-menu-dot" />
        <span className="quick-menu-dot" />
        <span className="quick-menu-dot" />
      </button>

      {open && (
        <div className="quick-menu-panel">
          <div className="quick-menu-brand">
            <div className="quick-menu-logo-wrap">
              <img src="/system-logo.jpeg" alt="شعار نظام الإشارة الإداري" />
            </div>
            <div>
              <p className="quick-menu-kicker">وصول سريع</p>
              <h3>نظام الإشارة الإداري</h3>
              <span>ابحث أو اختر الصفحة المطلوبة</span>
            </div>
          </div>

          <div className="quick-menu-search-wrap">
            <span>🔎</span>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="بحث ذكي: محلات، عمليات، محافظ، تقارير..."
              className="quick-menu-search"
            />
            {query && (
              <button
                type="button"
                className="quick-menu-clear"
                onClick={() => setQuery('')}
                aria-label="مسح البحث"
              >
                ×
              </button>
            )}
          </div>

          <div className="quick-menu-results">
            {results.length === 0 ? (
              <div className="quick-menu-empty">
                لا توجد نتيجة. جرّب اسم صفحة مثل العمليات أو المحلات.
              </div>
            ) : (
              results.map(page => {
                const active = location.pathname === page.path;
                return (
                  <button
                    key={page.path}
                    type="button"
                    onClick={() => goToPage(page.path)}
                    className={`quick-menu-item ${active ? 'quick-menu-item-active' : ''}`}
                  >
                    <span className="quick-menu-item-icon">{page.icon}</span>
                    <span className="quick-menu-item-body">
                      <strong>{page.title}</strong>
                      <small>{page.description}</small>
                    </span>
                    <span className="quick-menu-item-arrow">↗</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalQuickMenu;
